<script lang="ts" setup>
import type { IContentElement } from '@bosca/analytics-client-browser'
import {
  type BibleComponent, type BibleReference,
  type ComponentListComponent, type ContentComponent as CComponent,
  DisplayAnnotation,
  type MediaComponent, type Study,
  type TextComponent,
} from '~/utils/generated/graphql'
import type { ContentItemTypes } from '~/utils/graphql/graphql'

const props = defineProps<{
  item: ContentItemTypes
  components: (BibleComponent | ComponentListComponent | MediaComponent | TextComponent | CComponent)[]
  addMargin?: boolean | undefined
}>()

const contentItem = props.item
const nonBibleReferences = computed(() => props.components.filter(c => c.display?.annotation !== DisplayAnnotation.Bible))
const bibleReferences = computed(() => props.components.filter(c => c.display?.annotation === DisplayAnnotation.Bible))
const bibleReferenceButtons = computed(() => bibleReferences.value.flatMap(r => r.bibleReferences))
const bibleCarouselRef = ref()

function newContent(item: ContentItemTypes, _: BibleReference, index: number) {
  // TODO: think about what to send in terms of BibleReference
  if (item.__typename === 'Study' && (item as Study).session?.id) {
    return [
      { id: item.id, type: item.__typename, index: index },
      { id: (item as Study).session!.id, type: 'StudySession', index: index },
    ] as unknown as IContentElement[]
  }
  return [{ id: item.id, type: item.__typename, index: index }] as unknown as IContentElement[]
}
</script>

<template>
  <div>
    <div
      v-for="component in nonBibleReferences"
      :key="component.id"
      class="mt-8 font-serif"
    >
      <ContentComponent
        :item="item"
        :component="component"
        :add-margin="addMargin"
      />
    </div>
    <div v-if="bibleReferences.length > 0">
      <h3 class="font-signifier text-center">
        {{ $t('scriptureReferences') }}
      </h3>
      <div class="flex flex-wrap justify-items-center justify-center mt-8 gap-6 w-full">
        <PButton
          v-for="(bibleReference, index) in bibleReferenceButtons"
          :id="'scripture-reference-' + index"
          :key="bibleReference.usfm"
          :content="newContent(item, bibleReference, index)"
          :class="(index === bibleCarouselRef?.page - 1 ? 'text-blurple-600 dark:text-blurple-300' : 'text-black dark:text-white')"
          :click="() => bibleCarouselRef.select(index + 1)"
        >
          {{ bibleReference.human }}
        </PButton>
      </div>
      <div class="border-blurple-500 text-blurple-700 dark:text-blurple-300 rounded-lg border-2 mt-8">
        <UCarousel
          ref="bibleCarouselRef"
          v-slot="{ item: compItem }"
          :ui="{ item: 'basis-full' }"
          :items="bibleReferences"
        >
          <ContentComponent
            :item="contentItem"
            class="max-h-[500px] overflow-y-auto p-8"
            :component="compItem"
          />
        </UCarousel>
      </div>
    </div>
  </div>
</template>
